import type * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import type { VariantProps } from 'class-variance-authority';

import type { baseToggleVariants } from '../../../ui';
import type { DataAttributesProps } from '../../../utils/types';
import type { SelectOption } from '../common';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { forwardRef, useId } from 'react';

import {
  BaseToggleGroup as ShadToggleGroup,
  BaseToggleGroupItem as ShadToggleGroupItem,
} from '../../../ui/base-toggle';

type ToggleGroupProps = DataAttributesProps &
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof baseToggleVariants> & {
    name?: string;
    options: SelectOption[];
  };

export const ToggleGroup = forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(
  (
    {
      className,
      'data-testid': dataTestId,
      name,
      options,
      variant = 'outline',
      ...props
    },
    ref
  ) => {
    const id = useId();

    return (
      <ShadToggleGroup
        ref={ref}
        className={cn('gap-4', className)}
        variant={variant}
        {...props}
      >
        {options.map((option) => (
          <ShadToggleGroupItem
            key={`${id}-${option.value}`}
            className="flex-1"
            data-testid={`${dataTestId ?? name}-${option.value}`}
            disabled={option.disabled}
            value={option.value}
          >
            {option.label}
          </ShadToggleGroupItem>
        ))}
      </ShadToggleGroup>
    );
  }
);

ToggleGroup.displayName = 'ToggleGroup';
